// DynamicPriceModule.jsx
// ─────────────────────────────────────────────────────────────
// Entry point. Add one route:
//   <Route path="/dynamic-pricing/*" element={<DynamicPriceModule />} />
//
// Add to public/index.html <head>:
//   <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Outfit:wght@400;500;600;700;800&family=DM+Mono:wght@400;500;600&display=swap" rel="stylesheet">
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { NotificationProvider } from "./context/NotificationContext";
import GlobalStyles from "./components/GlobalStyles";

import DashboardPage       from "./pages/DashboardPage";
import RecommendationsPage from "./pages/RecommendationsPage";
import CompetitorsPage     from "./pages/CompetitorsPage";
import ReportsPage         from "./pages/ReportsPage";
import IntegrationPage     from "./pages/IntegrationPage";
import SettingsPage        from "./pages/SettingsPage";
import PropertyPage        from "./propertyCongiguration";

const NAV = [
  { key: "dashboard",       icon: "⚡", label: "Dashboard"      },
    { key: "property",       icon: "⚡", label: "Property"      },
  { key: "recommendations", icon: "💰", label: "Recommendations" },
  { key: "competitors",     icon: "🏨", label: "Competitors"     },
  { key: "reports",         icon: "📊", label: "Reports"         },
  { key: "integration",     icon: "🔗", label: "Integration"     },
  { key: "settings",        icon: "⚙️",  label: "Settings"        },
];

const PAGES = {
  dashboard:       DashboardPage,
  recommendations: RecommendationsPage,
  competitors:     CompetitorsPage,
  reports:         ReportsPage,
  integration:     IntegrationPage,
  settings:        SettingsPage,
  property :  PropertyPage,
};
const APP_HEADER_HEIGHT = 60; // adjust to your actual global header height
function DynamicPriceInner() {
  const [page, setPage] = useState("dashboard");
  const PageComponent   = PAGES[page];

  return (
    /*
      The outer div uses overflow:hidden + height:100vh so the inner
      flex column scroll works correctly even inside the PMS layout.
      The nav is sticky inside THIS container — not the browser window —
      so it always stays visible regardless of parent wrappers.
    */
<div
  className="dp-module"
  style={{
    display: "flex",
    flexDirection: "column",

    // Leave space for your existing PMS/global top header
    marginTop: APP_HEADER_HEIGHT,
    height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,

    overflow: "hidden",
    background: "#f1f5f9",
    fontFamily: "'Outfit', sans-serif",

    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0,
    padding: 0,

    width: "100%",
    boxSizing: "border-box",
  }}
>
      <GlobalStyles />

      {/* ── TOP NAVIGATION BAR ───────────────────────────────
          flexShrink:0 ensures it never collapses.
          No position:sticky needed — it's the first flex child.
      ──────────────────────────────────────────────────────── */}
      <header style={{
        background:   "#fff",
        borderBottom: "1px solid #e2e8f0",
        boxShadow:    "0 2px 10px rgba(0,0,0,0.07)",
        flexShrink:   0,             // never compress
        width:        "100%",
        zIndex:       "900",
      }}>
        <div style={{
          display:    "flex",
          alignItems: "center",
          padding:    "0 28px",
          
          height:     56,
        }}>

          {/* Brand mark */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 28, flexShrink: 0 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10,
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, boxShadow: "0 2px 8px rgba(37,99,235,0.30)",
            }}>
              💰
            </div>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#2563eb", letterSpacing: 1, textTransform: "uppercase" }}>
                Dynamic Pricing
              </div>
              <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>
                The Grand Chandigarh
              </div>
            </div>
          </div>

          {/* Vertical divider */}
          <div style={{ width: 1, height: 28, background: "#e2e8f0", marginRight: 24, flexShrink: 0 }} />

          {/* Nav items — scroll horizontally on very small screens */}
          <nav style={{
            display:    "flex",
            alignItems: "center",
            gap:        2,
            flex:       1,
            overflowX:  "auto",
            // Hide scrollbar but keep scroll
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}>
            {NAV.map(item => {
              const active = page === item.key;
              return (
                <button
                  key={item.key}
                  className="dp-nav-btn"
                  onClick={() => setPage(item.key)}
                  style={{
                    display:      "inline-flex",
                    alignItems:   "center",
                    gap:          7,
                    padding:      "0 16px",
                    height:       56,
                    background:   "transparent",
                    border:       "none",
                    // Bottom border acts as active indicator
                    borderBottom: active
                      ? "2.5px solid #2563eb"
                      : "2.5px solid transparent",
                    borderTop:    "2.5px solid transparent", // balance height
                    cursor:       "pointer",
                    color:        active ? "#2563eb" : "#64748b",
                    fontSize:     13,
                    fontWeight:   active ? 800 : 500,
                    fontFamily:   "'Outfit', sans-serif",
                    whiteSpace:   "nowrap",
                    transition:   "color 0.12s, border-color 0.12s",
                    outline:      "none",
                  }}
                >
                  <span style={{ fontSize: 15 }}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* AI version badge */}
          <div style={{ marginLeft: 16, flexShrink: 0 }}>
            <span style={{
              fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
              background: "#eff6ff", color: "#2563eb",
              border: "1.5px solid #bfdbfe",
              borderRadius: 20, padding: "3px 10px",
            }}>
              AI v1.0
            </span>
          </div>

        </div>
      </header>

      {/* ── PAGE CONTENT — scrolls independently ── */}
      <main style={{
        flex:       1,
        overflowY:  "auto",
        overflowX:  "hidden",
        minHeight:  0,   // required for flex child to shrink and scroll
      }}>
        <PageComponent onNav={setPage} />
      </main>
    </div>
  );
}

export default function DynamicPriceModule() {
  return (
    <NotificationProvider>
      <DynamicPriceInner />
    </NotificationProvider>
  );
}
